import React, { useState } from 'react';
import { X, Calendar, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { Slot } from '@/types/classRequests';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useUserAgendaConfig } from '@/features/calendar/hooks/useUserAgendaConfig';
import { getInitials } from '@/utils/avatarUtils';

interface ClassRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  slot: Slot;
  instructorId: string;
  instructorName: string;
  instructorAvatar?: string;
  instructorBeltRank?: string;
  selectedDate: Date;
  onSubmit: () => Promise<void>;
  isLoading?: boolean;
}

export const ClassRequestModal: React.FC<ClassRequestModalProps> = ({
  isOpen,
  onClose,
  slot,
  instructorId,
  instructorName,
  instructorAvatar,
  instructorBeltRank,
  selectedDate,
  onSubmit,
  isLoading = false
}) => {
  const { t, currentLanguage } = useI18n();
  const { config: instructorConfig } = useUserAgendaConfig(instructorId);

  if (!isOpen) return null;

  const formatTime = (timeString: string) => {
    // Handle different time formats
    let time = timeString;
    if (timeString.includes('T')) {
      time = timeString.split('T')[1]?.split('.')[0] || timeString;
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 12-hour format
    const ampm = hours >= 12 ? t('common.pm') : t('common.am');
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (date: Date) => {
    try {
      return date.toLocaleDateString(currentLanguage === 'pt' ? 'pt-BR' : 'en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return t('common.invalid_date');
    }
  };

  const formatPrice = () => {
    if (!instructorConfig?.class_value || instructorConfig.class_value === 0) {
      return t('class_request.price_info_default');
    }

    const currency = instructorConfig.currency || 'USD';
    const value = instructorConfig.class_value;
    
    // Format currency based on locale
    const formatter = new Intl.NumberFormat(
      currentLanguage === 'pt' ? 'pt-BR' : 'en-US',
      {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );

    return `${formatter.format(value)}/hora - ${t('class_request.payment_note')}`;
  };

  const handleSubmit = async () => {
    await onSubmit();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-black">
              {t('class_request.request_private_class')}
            </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Class Title */}
          <div>
            <h3 className="text-lg font-bold text-black mb-2">
              {t('class_request.private_class_gi')}
            </h3>
            <p className="text-sm text-gray-500">
              {formatDate(selectedDate)} - {formatTime(slot.start_time)} to {formatTime(slot.end_time)}
            </p>
          </div>

          {/* Instructor Info */}
          <div>
            <p className="text-sm font-medium text-black mb-2">{t('class_request.instructor')}</p>
            <div className="flex items-center gap-3">
              <FramedAvatar
                src={instructorAvatar}
                alt={instructorName}
                initials={getInitials(instructorName, '')}
                size={32}
                showFrame={true}
                beltRank={instructorBeltRank}
                frameSize={{ w: 40, h: 44 }}
              />
              <p className="text-sm text-gray-600">{instructorName}</p>
            </div>
          </div>

          {/* Duration */}
          <div>
            <p className="text-sm font-medium text-black mb-1">{t('class_request.duration')}</p>
            <p className="text-sm text-gray-600">
              {slot.duration_minutes} {t('class_request.minutes')}
            </p>
          </div>

          {/* Price Info */}
          <div>
            <p className="text-sm font-medium text-black mb-1">{t('class_request.price')}</p>
            <p className="text-sm text-gray-600">
              {formatPrice()}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1 border-gray-300 text-black hover:bg-gray-50 rounded-full"
            disabled={isLoading}
          >
{t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('class_request.submitting')}
              </div>
            ) : (
              t('class_request.submit_request')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
